import { useState, type Dispatch, type SetStateAction } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import type { CondicaoPagamento, RegraComissao, TabelaPreco } from '../../db/tipos'
import { apagarLocalEEnfileirar } from '../../sync/outbox'
// Reusa o formatador BR de 2 casas de moeda.ts (findings da revisao final,
// item 4): a logica (converterNumeroBR + toLocaleString 2 casas) e generica
// de numero, o nome so fala de moeda porque nasceu no campo de dinheiro. Sem
// isto o resumo mostrava o percentual cru do rascunho ("1%" em vez de "1,00%").
import { avisosDeComissaoPorTabela, type AvisoComissaoTabela } from '../../comissoes/planoTabela'
import { contarPedidosAbertosComTabela } from '../../pedidos/usoDaTabela'
import { confirmar } from '../../componentes/confirmar'
import { aoClicarComGuarda } from '../../componentes/guardaDeSaida'
import type { CondicaoRascunho, ErrosDaPolitica, RegraRascunho } from '../../representadas/rascunhoDaPolitica'
import { condicoesValidasVivas, rotuloCurtoDaCondicao } from '../../representadas/condicoesDaTabela'
import { textoComissaoDaTabela } from '../../comissoes/faixaResumo'
import { IconePorcento, IconeMoedas } from '../produtos/IconesDaFicha'
import { IconeMais } from './IconesDaPolitica'
import JanelaNovaTabela from './JanelaNovaTabela'
import CardRegra from './CardRegra'
import { ESTILO_CARTAO_NOVO, ESTILO_ICONE_CARTAO, ESTILO_SELO_VERDE, ESTILO_SELO_CINZA } from './estilosDaFornecedora'

// Faixa de cor por tabela (spec 27/09, 1.8). Pela POSICAO na lista em ordem
// de nome: estavel entre aberturas, sem coluna nova no banco.
const CORES = ['bg-teal-600', 'bg-sky-600', 'bg-violet-600', 'bg-amber-500', 'bg-rose-500', 'bg-emerald-600']


// Leva 2 (spec 2026-09-29): o card da tabela so MOSTRA -- nome, selo,
// Produtos | Comissao, Prazos, e os botoes Editar e Excluir tabela. Prazos
// ligados e comissao propria sairam daqui para o quadro da tela Editar. A aba
// continua editando so a comissao PADRAO da fornecedora (card de cima).
export default function TabelasEComissao({
  representadaId, regras, setRegras, regrasSalvas, erros, editavel, condicoes,
}: {
  representadaId: string
  regras: RegraRascunho[]
  setRegras: Dispatch<SetStateAction<RegraRascunho[]>>
  regrasSalvas: RegraComissao[]
  erros: ErrosDaPolitica
  editavel: boolean
  condicoes: CondicaoRascunho[]
}) {
  const navegar = useNavigate()
  const tabelas = useLiveQuery(
    () => banco.tabelas_preco.where('representada_id').equals(representadaId).filter((t) => t.apagado_em === null).sortBy('nome'),
    [representadaId],
  )
  const qtdProdutos = useLiveQuery(async () => {
    const m = new Map<string, number>()
    for (const t of tabelas ?? []) {
      m.set(t.id, await banco.precos.where('tabela_preco_id').equals(t.id).filter((p) => p.apagado_em === null).count())
    }
    return m
  }, [tabelas], new Map<string, number>())
  const avisos = useLiveQuery(() => avisosDeComissaoPorTabela(representadaId), [representadaId], new Map<string, AvisoComissaoTabela>())
  const [janela, setJanela] = useState(false)

  const padrao = regras.find((r) => r.tabelaPrecoId === null && !r.removida)
  const padraoSalva = regrasSalvas.find((r) => r.tabela_preco_id === null && r.apagado_em === null)
  const propriaDa = (tabelaId: string) => regrasSalvas.find((r) => r.tabela_preco_id === tabelaId && r.apagado_em === null)
  const mudar = (chave: string, p: Partial<RegraRascunho>) => setRegras((l) => l.map((r) => (r.chave === chave ? { ...r, ...p } : r)))
  const comoCondicoes = condicoes.filter((c) => !c.removida)
    .map((c) => ({ id: c.chave, descricao: c.descricao, apagado_em: null }) as unknown as CondicaoPagamento)

  async function aoExcluirTabela(t: TabelaPreco) {
    const abertos = await contarPedidosAbertosComTabela(t.id)
    const pergunta = abertos > 0
      ? `"${t.nome}" está em ${abertos} pedido(s) aberto(s). Os itens já lançados ficam com o preço deles, mas você não vai conseguir adicionar mais produtos dela. Excluir mesmo assim?`
      : `Excluir a tabela de preço "${t.nome}"?`
    if (!(await confirmar(pergunta, { botao: 'Excluir', perigo: true }))) return
    await apagarLocalEEnfileirar('tabelas_preco', t.id)
  }

  const antesDeNavegar = () => navegar(`/representadas/${representadaId}?aba=comercial`, { replace: true })

  return (
    <>
      {padrao && (
        <div className={`${ESTILO_CARTAO_NOVO} space-y-2`} data-testid="regra-padrao">
          <div className="flex items-start gap-2">
            <span className={ESTILO_ICONE_CARTAO}><IconePorcento tamanho={18} /></span>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-800">Comissão padrão da fornecedora</h3>
              <p className="text-[11px] leading-tight text-slate-500">Vale para as tabelas sem comissão própria.</p>
            </div>
          </div>
          <CardRegra representadaId={representadaId} rascunho={padrao} regraSalva={padraoSalva}
            erro={erros[padrao.chave]} editavel={editavel} aoMudar={(p) => mudar(padrao.chave, p)} />
        </div>
      )}

      <div className={`${ESTILO_CARTAO_NOVO} space-y-2`}>
        <div className="flex items-start gap-2">
          <span className={ESTILO_ICONE_CARTAO}><IconeMoedas tamanho={18} /></span>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-slate-800">Tabelas de preço da fornecedora</h3>
            <p className="text-[11px] leading-tight text-slate-500">Tabelas desta fornecedora e a comissão de cada uma</p>
          </div>
          <button type="button" onClick={() => setJanela(true)}
            className="shrink-0 flex items-center gap-1 rounded-lg bg-teal-700 text-white px-2.5 py-1.5 text-xs font-medium">
            <IconeMais tamanho={14} /> Tabela
          </button>
        </div>
        {tabelas?.length === 0 && <p className="text-xs text-slate-500">Nenhuma tabela. Toque em + Tabela para importar a planilha ou criar do zero.</p>}
        {tabelas?.map((t, i) => {
          const propria = propriaDa(t.id)
          const aviso = avisos.get(t.id)
          const semComissao = aviso?.pendentes ?? 0
          const validas = condicoesValidasVivas(t.condicoes_validas, comoCondicoes)
          const usada = propria ?? padraoSalva
          const usaExplicitos = usada?.tipo === 'pct_por_item' && aviso?.regraId === usada.id
          const resumoComissao = usada
            ? textoComissaoDaTabela({
                tipo: usada.tipo, pctPadrao: usada.pct_padrao,
                explicitos: usaExplicitos ? aviso.explicitos : [],
                algumSemExplicito: usaExplicitos ? aviso.algumSemExplicito : true,
              }) + (propria ? '' : ' (padrão da fornecedora)')
            : 'Comissão —'
          const destinoTabela = `/tabelas/${t.id}`
          return (
            <div key={t.id} data-testid={`cartao-tabela-${t.id}`}
              className="relative rounded-xl border border-slate-200 bg-white shadow-[0_4px_12px_-4px_rgba(15,23,42,0.14)] pl-3 pr-2 py-2 overflow-hidden space-y-1.5">
              <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${CORES[i % CORES.length]}`} aria-hidden />
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800 break-words">{t.nome}</span>
                <span className={t.ativa ? ESTILO_SELO_VERDE : ESTILO_SELO_CINZA}>{t.ativa ? 'Ativa' : 'Inativa'}</span>
              </div>
              {/* 360px: tres colunas do desenho nao cabem -- Produtos | Comissao
                  numa linha, Prazos numa linha inteira embaixo (spec 1). */}
              <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2 py-1.5">
                <div>
                  <p className="text-[11px] text-slate-500">Produtos</p>
                  <p className="text-sm font-semibold text-slate-800">{qtdProdutos.get(t.id) ?? '…'}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] text-slate-500">Comissão</p>
                  <p className="text-xs font-semibold text-slate-800 break-words">{resumoComissao.replace(/^Comissão /, '')}</p>
                </div>
              </div>
              {validas.length > 0 && (
                <p className="flex flex-wrap items-center gap-1" aria-label={`Prazos: ${validas.map((c) => c.descricao).join(', ')}`}>
                  <span className="text-[11px] text-slate-500">Prazos:</span>
                  {validas.map((c) => (
                    <span key={c.id} className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-sky-50 text-sky-800">{rotuloCurtoDaCondicao(c.descricao)}</span>
                  ))}
                </p>
              )}
              {(aviso?.regraSemPercentual || semComissao > 0) && (
                <div className="flex flex-wrap gap-1">
                  {aviso?.regraSemPercentual && <span className="text-[11px] bg-red-100 text-red-800 rounded-full px-2 py-0.5">comissão sem % — paga 0%</span>}
                  {semComissao > 0 && <span className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{semComissao} sem comissão</span>}
                </div>
              )}
              <div className="flex gap-2">
                <Link to={destinoTabela} onClick={aoClicarComGuarda(navegar, destinoTabela, antesDeNavegar)}
                  className="flex-1 text-center rounded-lg border border-teal-700 text-teal-800 py-1.5 text-xs font-medium">
                  Editar
                </Link>
                <button type="button" onClick={() => void aoExcluirTabela(t)}
                  className="flex-1 rounded-lg border border-red-300 text-red-700 py-1.5 text-xs font-medium">
                  Excluir tabela
                </button>
              </div>
            </div>
          )
        })}
      </div>
      {janela && <JanelaNovaTabela representadaId={representadaId} aoFechar={() => setJanela(false)} />}
    </>
  )
}
